import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { MicrosoftSignInButton } from './MicrosoftSignInButton';
import { RateLimitCountdown } from '../auth/RateLimitCountdown';
import { useRateLimitCountdown } from '../../hooks/useRateLimitCountdown';
import { formatRateLimitContext, parseBlockedUntil } from '../../utils/rateLimit';
import { cn } from '../../lib/utils';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

export function LoginPanel({
  onSignIn,
  isLoading,
  errorMessage,
  rateLimitBlock = null,
  className,
}) {
  const expiresAt = rateLimitBlock
    ? parseBlockedUntil(rateLimitBlock.blockedUntil)
    : null;
  const { canRetry } = useRateLimitCountdown({ expiresAt });
  const isRateLimited = Boolean(rateLimitBlock && expiresAt && !canRetry);

  const handleSignIn = useCallback(
    (event) => {
      if (isRateLimited || isLoading) {
        event?.preventDefault?.();
        return;
      }
      onSignIn?.(event);
    },
    [isRateLimited, isLoading, onSignIn]
  );

  return (
    <div
      className={cn(
        'relative mx-auto w-full min-w-0 max-w-[28rem]',
        'landing-enter animate-fade-in-up [animation-delay:400ms]',
        className
      )}
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border p-4 sm:p-5 md:p-6 laptop:p-7',
          'border-white/40 bg-white/70 shadow-[0_0_60px_-12px_rgba(59,130,246,0.35)] backdrop-blur-xl',
          'dark:border-blue-500/20 dark:bg-gray-900/75 dark:shadow-[0_0_60px_-12px_rgba(59,130,246,0.2)]',
          'ring-1 ring-slate-200/50 dark:ring-blue-500/10',
          'motion-reduce:animate-none laptop:motion-reduce:animate-none laptop:animate-glow-pulse'
        )}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent"
          aria-hidden="true"
        />

        <div className="mb-3 flex items-center gap-1.5 sm:mb-4 sm:gap-2 laptop:mb-5">
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground sm:text-[11px] sm:tracking-[0.12em]">
            Secure workspace
          </span>
        </div>

        <div className="space-y-3 sm:space-y-4 md:space-y-5 laptop:space-y-6">
          <div className="space-y-1 sm:space-y-1.5">
            <h2 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl md:text-2xl laptop:text-3xl">
              Welcome back
            </h2>
            <p className="text-xs leading-snug text-muted-foreground sm:text-sm md:text-base">
              Sign in with your organization account.
            </p>
          </div>

          {isRateLimited ? (
            <Alert variant="destructive" className="text-left">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>Please slow down</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  {formatRateLimitContext({
                    limit: rateLimitBlock.limit,
                    windowSeconds: rateLimitBlock.windowSeconds,
                  })}
                </p>
                <RateLimitCountdown
                  blockedUntil={rateLimitBlock.blockedUntil}
                  size="compact"
                  className="border-destructive/20 bg-destructive/5"
                />
              </AlertDescription>
            </Alert>
          ) : errorMessage ? (
            <Alert variant="destructive" className="text-left">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>Sign-in unavailable</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <div className="w-full min-w-0 space-y-2 sm:space-y-2.5">
            <p className="text-center text-xs font-medium text-muted-foreground sm:text-sm">
              Single sign-on
            </p>
            <MicrosoftSignInButton
              onClick={handleSignIn}
              isLoading={isLoading}
              disabled={isRateLimited}
              className="relative z-10 w-full max-w-none"
            />
          </div>

          <p className="text-[10px] leading-relaxed text-muted-foreground sm:text-ds-caption">
            By signing in, you agree to our{' '}
            <Link
              to="/terms"
              className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-2 hover:decoration-blue-600 dark:text-blue-400"
            >
              terms
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-2 hover:decoration-blue-600 dark:text-blue-400"
            >
              privacy policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
